import { ArrowLeft, Building2, Check } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/Button.jsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/Card.jsx";
import { Input } from "../components/ui/Input.jsx";
import { Label } from "../components/ui/Label.jsx";
import { useToast } from "../hooks/useToast.js";
import {
  createCompany,
  getIndustryTemplates,
} from "../services/companyService.js";
import { useFormValidation } from "../hooks/useFormValidation.js";
import { VALIDATION_RULES } from "../utils/validation/validationRules.js";
import { cn } from "../utils/className.js";

const companyValidation = {
  company_name: VALIDATION_RULES.company_name,
  company_email: {
    required: "Company email is required",
    pattern: {
      regex: /^[a-zA-Z0-9][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      message: "Invalid email format",
    },
  },
  company_phone: VALIDATION_RULES.phone_required,
  company_address: VALIDATION_RULES.address,
};

export default function AddCompany() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState("other");

  const {
    form: formData,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateAll,
  } = useFormValidation(
    {
      company_name: "",
      company_email: "",
      company_phone: "",
      company_address: "",
    },
    companyValidation
  );

  const { company_name, company_email, company_phone, company_address } =
    formData;

  useEffect(() => {
    (async () => {
      try {
        const res = await getIndustryTemplates();
        setIndustries(res.data || []);
      } catch {
        setIndustries([]);
      }
    })();
  }, []);

  const handleSubmit = async () => {
    if (!validateAll()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form.",
        variant: "destructive",
      });
      return;
    }

    setIsRedirecting(true);

    try {
      const res = await createCompany({
        ...formData,
        industry_type: selectedIndustry,
      });
      if (res.success) {
        toast({ title: "Success", description: res.message });
        navigate("/dashboard");
      } else {
        toast({
          title: "Failed",
          description: res?.message || "Unable to create company.",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Server error",
        variant: "destructive",
      });
    } finally {
      setIsRedirecting(false);
    }
  };

  const selectedTemplate = industries.find((i) => i.key === selectedIndustry);

  return (
    <div className="flex flex-col justify-between p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="rounded-full"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-lg font-bold">Create Company</h1>
      </div>

      <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
        {/* Industry Selector */}
        <Card className="bg-transparent border-none shadow-none">
          <CardHeader className="p-0 pb-2">
            <CardTitle className="text-base font-bold">
              Select Your Industry
            </CardTitle>
            <p className="text-[11px] text-gray-500 mt-0.5">
              We'll pre-configure lead form fields based on your industry. You
              can customize everything later in Settings.
            </p>
          </CardHeader>

          <CardContent className="p-0">
            <div className="grid grid-cols-2 gap-2">
              {industries.map((industry) => (
                <button
                  key={industry.key}
                  type="button"
                  onClick={() => setSelectedIndustry(industry.key)}
                  className={cn(
                    "relative text-left p-3 rounded-xl border-2 transition-all text-xs",
                    selectedIndustry === industry.key
                      ? "border-[#00a8b4] bg-[#00a8b4]/5 shadow-sm"
                      : "border-gray-100 bg-white hover:border-gray-200"
                  )}
                >
                  {selectedIndustry === industry.key && (
                    <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-[#00a8b4] flex items-center justify-center">
                      <Check size={10} className="text-white" />
                    </div>
                  )}
                  <p className="font-semibold text-gray-800 text-xs leading-tight">
                    {industry.label}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {industry.field_count} fields pre-configured
                  </p>
                </button>
              ))}
            </div>

            {/* Preview of selected template */}
            {selectedTemplate && (
              <div className="mt-3 p-3 bg-gray-50 rounded-xl">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Pre-configured fields for {selectedTemplate.label}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedTemplate.fields.map((f, idx) => (
                    <span
                      key={idx}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-600"
                    >
                      {f.label}{" "}
                      <span className="text-gray-400">
                        ({f.option_count})
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Company Info */}
        <Card className="bg-transparent border-none shadow-none">
          <CardHeader className="p-0 pb-2">
            <CardTitle className="text-base font-bold">
              Company Information
            </CardTitle>
          </CardHeader>

          <CardContent className="p-2 pb-4 space-y-1 bg-white border-2 rounded-xl shadow-sm border-blue-100/50">
            {/* Company Name */}
            <div>
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                name="company_name"
                placeholder="Enter company name"
                value={company_name}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                className={`h-8 text-xs ${
                  errors.company_name ? "border-red-500" : ""
                }`}
              />
              {touched.company_name && errors.company_name && (
                <p className="mt-1 text-[11px] text-red-500">
                  {errors.company_name}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="company_email">Company Email *</Label>
              <Input
                id="company_email"
                name="company_email"
                type="email"
                placeholder="email@company.com"
                value={company_email}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                className={`h-8 text-xs ${
                  errors.company_email ? "border-red-500" : ""
                }`}
              />
              {touched.company_email && errors.company_email && (
                <p className="mt-1 text-[11px] text-red-500">
                  {errors.company_email}
                </p>
              )}
            </div>

            {/* Phone */}
            <div className="space-y-1">
              <Label htmlFor="company_phone">Company Phone No. *</Label>
              <Input
                id="company_phone"
                name="company_phone"
                type="tel"
                placeholder="9876543210"
                value={company_phone}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                maxLength={10}
                className={`h-8 text-xs ${
                  errors.company_phone ? "border-red-500" : ""
                }`}
              />
              {touched.company_phone && errors.company_phone && (
                <p className="mt-1 text-[11px] text-red-500">
                  {errors.company_phone}
                </p>
              )}
            </div>

            {/* Address */}
            <div className="space-y-1">
              <Label htmlFor="company_address">Company Address *</Label>
              <Input
                id="company_address"
                name="company_address"
                placeholder="Enter company address"
                value={company_address}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                className={`h-8 text-xs ${
                  errors.company_address ? "border-red-500" : ""
                }`}
              />
              {touched.company_address && errors.company_address && (
                <p className="mt-1 text-[11px] text-red-500">
                  {errors.company_address}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            variant="outline"
            className="h-8 px-3 text-xs rounded-full"
            onClick={() => navigate("/")}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isRedirecting}
            className="h-8 px-3 text-xs text-white rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391]"
          >
            <Building2 className="w-4 h-4 mr-1" />
            {isRedirecting ? "Creating..." : "Create Company"}
          </Button>
        </div>
      </form>
    </div>
  );
}
